import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { siteConfig } from '@/config/site';
import { getCanonicalUrl } from '@/hooks/useCanonicalUrl';

export interface MetaData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noIndex?: boolean;
}

type MetaSetter = (meta: MetaData) => void;

const MetaContext = createContext<MetaSetter | null>(null);

/**
 * Hook for imperative meta updates (rare cases).
 * Prefer the declarative <Meta> component instead.
 */
export function useSetMeta(): MetaSetter {
  const setter = useContext(MetaContext);
  if (!setter) return () => {};
  return setter;
}

const BASE_URL = siteConfig.url;
const DEFAULT_OG_IMAGE = siteConfig.seo.ogImage;

function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function applyMeta(meta: MetaData, lang: string, canonicalUrl: string) {
  const {
    title,
    description,
    keywords,
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    author = siteConfig.author.name,
    publishedTime,
    modifiedTime,
    noIndex = false,
  } = meta;

  document.title = title;
  document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';

  setMetaTag('description', description);
  if (keywords) setMetaTag('keywords', keywords);
  setMetaTag('author', author);
  setMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

  const fullImage = image.startsWith('http') ? image : `${BASE_URL}${image}`;
  setMetaTag('og:title', title, true);
  setMetaTag('og:description', description, true);
  setMetaTag('og:image', fullImage, true);
  setMetaTag('og:url', canonicalUrl, true);
  setMetaTag('og:type', type, true);
  setMetaTag('og:site_name', siteConfig.name, true);
  setMetaTag('og:locale', lang === 'zh' ? 'zh_CN' : 'en_US', true);
  setMetaTag('og:locale:alternate', lang === 'zh' ? 'en_US' : 'zh_CN', true);

  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', title);
  setMetaTag('twitter:description', description);
  setMetaTag('twitter:image', fullImage);
  setMetaTag('twitter:creator', '@wanghaisheng');
  setMetaTag('twitter:site', '@borninsea');

  if (type === 'article') {
    setMetaTag('article:author', author, true);
    if (publishedTime) setMetaTag('article:published_time', publishedTime, true);
    if (modifiedTime) setMetaTag('article:modified_time', modifiedTime, true);
  }
}

const ROUTE_META: Record<string, { zh: MetaData; en: MetaData }> = {
  '/': {
    zh: { title: '王海生 borninsea | 从系统崩溃到重建自我 - 创伤疗愈与AI创业探索', description: '王海生的个人网站。12年AI+医疗产品经验，40年创伤探索之路。', keywords: '王海生,borninsea,创伤疗愈,AI创业,产品经理' },
    en: { title: 'Wang Haisheng borninsea | From System Crash to Self-Rebuild', description: 'Personal website of Wang Haisheng. 12 years in AI+Healthcare, 40 years of trauma exploration.', keywords: 'Wang Haisheng,borninsea,trauma healing,AI entrepreneurship' },
  },
  '/about': {
    zh: { title: '关于我 | 王海生 borninsea', description: '了解王海生的三重身份：创伤探索者、产品经理、创业者。', keywords: '王海生,关于我,创伤探索者,产品经理' },
    en: { title: 'About Me | Wang Haisheng borninsea', description: "Learn about Wang Haisheng's three identities: Trauma Explorer, Product Manager, Entrepreneur.", keywords: 'Wang Haisheng,about,trauma explorer,product manager' },
  },
  '/challenge100': {
    zh: { title: 'Challenge 100 系统 | borninsea', description: '在 Spy、Launch、Ship 和 Grow 四个阶段里，做 100 次实战尝试。', keywords: 'Challenge100,Spy100,Launch100,Ship100,Grow100' },
    en: { title: 'Challenge 100 System | borninsea', description: 'Make 100 attempts across Spy, Launch, Ship, and Grow phases.', keywords: 'Challenge100,Spy100,Launch100,Ship100,Grow100' },
  },
  '/spy100': {
    zh: { title: 'Spy 100 · 侦察与情报 | borninsea', description: '观察趋势、收集灵感、做最小成本的情报验证。', keywords: 'Spy100,竞品分析,市场调研' },
    en: { title: 'Spy 100 · Research & Intelligence | borninsea', description: 'Observe trends, collect inspiration, validate with minimal cost.', keywords: 'Spy100,competitor analysis,market research' },
  },
  '/launch100': {
    zh: { title: 'Launch 100 · 快速上线 | borninsea', description: '用最小可行版本把想法推出去。', keywords: 'Launch100,MVP,快速原型' },
    en: { title: 'Launch 100 · Rapid Launch | borninsea', description: 'Push ideas out with minimum viable versions.', keywords: 'Launch100,MVP,rapid prototyping' },
  },
  '/ship100': {
    zh: { title: 'Ship 100 · 真正交付 | borninsea', description: '做出真正可用、可靠的版本。', keywords: 'Ship100,产品交付,产品打磨' },
    en: { title: 'Ship 100 · Real Delivery | borninsea', description: 'Create truly usable, reliable products.', keywords: 'Ship100,product delivery' },
  },
  '/grow100': {
    zh: { title: 'Grow 100 · 持续增长 | borninsea', description: '找到可复制的增长机制。', keywords: 'Grow100,增长黑客,产品增长' },
    en: { title: 'Grow 100 · Sustainable Growth | borninsea', description: 'Find replicable growth mechanisms.', keywords: 'Grow100,growth hacking' },
  },
  '/challenge100/journey': {
    zh: { title: 'Challenge 100 时间线 | borninsea', description: '用时间线的形式，记录每一个项目的关键节点。', keywords: 'Challenge100,时间线,项目进度' },
    en: { title: 'Challenge 100 Timeline | borninsea', description: 'Track key milestones of each project in timeline format.', keywords: 'Challenge100,timeline,milestones' },
  },
  '/pearl': {
    zh: { title: 'Pearl Framework - 从创伤沙砾到天赋珍珠 | borninsea', description: 'Pearl Framework 是一个创新的创伤知情工程学方法论。', keywords: 'Pearl Framework,创伤知情工程,TIE-ASM' },
    en: { title: 'Pearl Framework - From Trauma Grit to Talent Pearl | borninsea', description: 'Pearl Framework is an innovative Trauma-Informed Engineering methodology.', keywords: 'Pearl Framework,Trauma-Informed Engineering,TIE-ASM' },
  },
  '/cold-start': {
    zh: { title: '心力耗竭冷启动法 | Pearl Framework | borninsea', description: '从心力耗竭中冷启动的核心方法论。', keywords: '冷启动,心力耗竭,Pearl Framework' },
    en: { title: 'Cold Start Method | Pearl Framework | borninsea', description: 'Core methodology for rebooting from complete energy depletion.', keywords: 'Cold Start,energy depletion,Pearl Framework' },
  },
};

function normalizeRoute(pathname: string): string {
  let p = pathname.replace(/^\/zh/, '') || '/';
  if (p !== '/' && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/**
 * MetaManager: centralized meta tag management in RootLayout.
 * All document.* side effects are isolated here — page components stay pure.
 */
export function MetaManager({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const overrideRef = useRef<MetaData | null>(null);

  const lang = location.pathname.startsWith('/zh') ? 'zh' : 'en';
  const route = normalizeRoute(location.pathname);
  const canonicalUrl = getCanonicalUrl(location.pathname, lang);

  // Reset override on route change
  useEffect(() => {
    overrideRef.current = null;
  }, [location.pathname]);

  // Apply meta after render
  useEffect(() => {
    const timer = setTimeout(() => {
      const meta = overrideRef.current || ROUTE_META[route]?.[lang as 'zh' | 'en'];
      if (meta) applyMeta(meta, lang, canonicalUrl);
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname, i18n.language, route, lang, canonicalUrl]);

  const setMeta: MetaSetter = useCallback((meta: MetaData) => {
    overrideRef.current = meta;
    const currentLang = location.pathname.startsWith('/zh') ? 'zh' : 'en';
    applyMeta(meta, currentLang, getCanonicalUrl(location.pathname, currentLang));
  }, [location.pathname]);

  return (
    <MetaContext.Provider value={setMeta}>
      {children}
    </MetaContext.Provider>
  );
}

/**
 * Declarative Meta component — pages render <Meta title="..." description="..." />
 * instead of calling useEffect + useSetMeta manually.
 * The component is pure from the page's perspective (no useEffect in page code).
 */
export function Meta(props: MetaData) {
  const setter = useContext(MetaContext);

  useEffect(() => {
    if (setter) setter(props);
  }, [setter, props.title, props.description, props.keywords, props.image, props.type, props.noIndex]);

  return null;
}
